import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { integrationActions } from '../actions/integration.actions';
import { IAbacusConnectBody } from '../models/integration/abacus-connect.model';
import { ITopalConnectBody } from '../models/integration/topal-connect.model';
import { IAccountoConnectBody } from '../models/integration/accounto-connect.model';
import { IProffixConnectBody } from '../models/integration/proffix-connect.model';
import { ICompanyMappingSuggestion } from '../models/integration/company-mapping-suggestion.model';
import {
    IAumicoCompanyWithMapping,
    IUpsertConnectionMappingBody,
} from '../models/integration/connection-mapping.model';
import { IAbacusImportBody } from '../models/integration/abacus-import.model';
import { ITopalImportBody } from '../models/integration/topal-import.model';
import { IAccountoImportBody } from '../models/integration/accounto-import.model';
import { IProffixImportBody } from '../models/integration/proffix-import.model';
import {
    ID365BcImportBody,
    IFiscalYear,
} from '../models/integration/d365bc-import.model';
import { IJournalImportResult } from '../models/integration/journal-import.model';
import { IIntegrationConnection } from '../models/integration/integration-connection.model';
import { IntegrationVendor } from '../models/integration/integration-vendor.enum';
import {
    SelectedClosingFolder,
    SharepointConnection,
} from '../components/sharepoint-folders/sharepoint-folders.types';
import { PickedSharepointFolder } from '../services/sharepoint-file-picker.service';
import {
    IAccountoJournalImportState,
    IExternalCompaniesState,
    IntegrationState,
    IPendingSetup,
    IProffixJournalImportState,
    ISingleEditState,
    ITenantDetailState,
} from '../reducer/integration.reducer';
import {
    selectAllConnections,
    selectAutoMatchBannerForConnection,
    selectConnectionById,
    selectConnectionsByVendor,
    selectAbacusImportError,
    selectAbacusImportResult,
    selectAbacusImportStatus,
    selectTopalImportError,
    selectTopalImportResult,
    selectTopalImportStatus,
    selectAccountoImportError,
    selectAccountoImportResult,
    selectAccountoImportStatus,
    selectAccountoJournalImportState,
    selectProffixImportError,
    selectProffixImportResult,
    selectProffixImportStatus,
    selectProffixJournalImportState,
    selectD365BcFiscalYears,
    selectD365BcFiscalYearsLoading,
    selectD365BcImportError,
    selectD365BcImportResult,
    selectD365BcImportStatus,
    selectExternalCompaniesItems,
    selectExternalCompaniesState,
    selectFirstConnectionByVendor,
    selectIsAutoMatchRunning,
    selectIsConnectionHealthyById,
    selectIsExternalCompaniesLoading,
    selectIsLoading,
    selectIsTenantDetailLoading,
    selectLoadError,
    selectPendingSetup,
    selectPendingSetupCreatedConnectionId,
    selectPendingSetupError,
    selectPendingSetupStep,
    selectPendingSetupVendor,
    selectSingleEditIsActive,
    selectSingleEditState,
    selectTenantDetailCompanies,
    selectTenantDetailError,
    selectTenantDetailState,
    selectSharepointConnection,
    selectSharepointFolders,
    selectSharepointLoading,
    selectSharepointError,
    selectSharepointPickerBaseUrl,
    selectSharepointPickerToken,
    selectSharepointPickerTokenError,
} from '../selectors/integration.selectors';

/**
 * Facade for the integration module. Pages consume this; never reach into the Store directly.
 */
@Injectable({ providedIn: 'root' })
export class IntegrationProvider {
    public constructor(private readonly store: Store<IntegrationState>) {}

    // ── Selectors as Observables ──

    public getConnections$(): Observable<IIntegrationConnection[]> {
        return this.store.select(selectAllConnections);
    }

    public getConnectionById$(connectionId: string): Observable<IIntegrationConnection | null> {
        return this.store.select(selectConnectionById(connectionId));
    }

    public getConnectionsByVendor$(vendor: IntegrationVendor): Observable<IIntegrationConnection[]> {
        return this.store.select(selectConnectionsByVendor(vendor));
    }

    public getFirstConnectionByVendor$(vendor: IntegrationVendor): Observable<IIntegrationConnection | null> {
        return this.store.select(selectFirstConnectionByVendor(vendor));
    }

    public getIsLoading$(): Observable<boolean> {
        return this.store.select(selectIsLoading);
    }

    public getLoadError$(): Observable<string | null> {
        return this.store.select(selectLoadError);
    }

    public getPendingSetup$(): Observable<IPendingSetup> {
        return this.store.select(selectPendingSetup);
    }

    public getPendingSetupStep$(): Observable<IPendingSetup['step']> {
        return this.store.select(selectPendingSetupStep);
    }

    public getPendingSetupVendor$(): Observable<IntegrationVendor | null> {
        return this.store.select(selectPendingSetupVendor);
    }

    public getPendingSetupError$(): Observable<string | null> {
        return this.store.select(selectPendingSetupError);
    }

    public getPendingSetupCreatedConnectionId$(): Observable<string | null> {
        return this.store.select(selectPendingSetupCreatedConnectionId);
    }

    /**
     * Manage modal's BC company list. Owned by `loadExternalCompanies` and
     * disjoint from the single edit session, so this stream is stable across
     * any single edit modal open/close.
     */
    public getExternalCompaniesState$(): Observable<IExternalCompaniesState> {
        return this.store.select(selectExternalCompaniesState);
    }

    public getExternalCompanies$(): Observable<ICompanyMappingSuggestion[]> {
        return this.store.select(selectExternalCompaniesItems);
    }

    public isExternalCompaniesLoading$(): Observable<boolean> {
        return this.store.select(selectIsExternalCompaniesLoading);
    }

    /** Active single edit session: the BC company currently being remapped, if any. */
    public getSingleEditState$(): Observable<ISingleEditState> {
        return this.store.select(selectSingleEditState);
    }

    public isSingleEditActive$(): Observable<boolean> {
        return this.store.select(selectSingleEditIsActive);
    }

    // ── Action dispatchers ──

    public loadConnections(): void {
        this.store.dispatch(integrationActions.loadConnections());
    }

    /**
     * Starts the full connect-flow for a vendor. For D365 BC the BE handles consent →
     * test → create atomically; the FE just provides form input upfront.
     */
    public startConnect(
        vendor: IntegrationVendor,
        environmentName: string,
        displayName?: string,
    ): void {
        this.store.dispatch(
            integrationActions.initiateConsent({ vendor, environmentName, displayName }),
        );
    }

    public cancelSetup(): void {
        this.store.dispatch(integrationActions.cancelSetup());
    }

    /**
     * Starts the Abacus connect flow. No popup. Single HTTP roundtrip. The
     * reducer flips pendingSetup → `awaiting_consent` immediately so the modal
     * can render its testing spinner; the effect then POSTs the credentials
     * and on success → `created` via the same code path D365 BC uses.
     */
    public startAbacusConnect(body: IAbacusConnectBody): void {
        this.store.dispatch(integrationActions.initiateAbacusConnect({ body }));
    }

    /**
     * Starts the Topal connect flow. No popup. Single HTTP roundtrip. Mirrors
     * `startAbacusConnect`: the reducer flips pendingSetup → `awaiting_consent`
     * immediately so the modal renders its testing spinner; the effect POSTs the
     * credentials and on success → `created`.
     */
    public startTopalConnect(body: ITopalConnectBody): void {
        this.store.dispatch(integrationActions.initiateTopalConnect({ body }));
    }

    /**
     * Starts the Accounto connect flow. No popup. Single HTTP roundtrip. Mirrors
     * `startTopalConnect`: the reducer flips pendingSetup → `awaiting_consent`
     * immediately so the modal renders its testing spinner; the effect POSTs the
     * key and on success → `created`.
     */
    public startAccountoConnect(body: IAccountoConnectBody): void {
        this.store.dispatch(integrationActions.initiateAccountoConnect({ body }));
    }

    /**
     * Starts the Proffix connect flow. No popup. Single HTTP roundtrip. Mirrors
     * `startTopalConnect`: the reducer flips pendingSetup → `awaiting_consent`
     * immediately so the modal renders its testing spinner; the effect POSTs the
     * credentials and on success → `created`.
     */
    public startProffixConnect(body: IProffixConnectBody): void {
        this.store.dispatch(integrationActions.initiateProffixConnect({ body }));
    }

    public testExistingConnection(connectionId: string): void {
        this.store.dispatch(integrationActions.testExistingConnection({ connectionId }));
    }

    public disconnect(connectionId: string): void {
        this.store.dispatch(integrationActions.disconnect({ connectionId }));
    }

    // ── Phase 2: Mapping ──

    public loadExternalCompanies(connectionId: string): void {
        this.store.dispatch(integrationActions.loadExternalCompanies({ connectionId }));
    }

    public cancelMapping(): void {
        this.store.dispatch(integrationActions.mappingCancel());
    }

    /**
     * Open the mapping wizard for exactly one external company. After the user
     * makes a decision the wizard auto closes (no summary, no "X of Y").
     */
    public startSingleEdit(
        connectionId: string,
        vendor: IntegrationVendor,
        suggestion: ICompanyMappingSuggestion,
    ): void {
        this.store.dispatch(
            integrationActions.mappingStartSingleEdit({ connectionId, vendor, suggestion }),
        );
    }

    // ── Tenant detail (D2): Aumico Companies linked to a connection ────────

    public getTenantDetailState$(): Observable<ITenantDetailState> {
        return this.store.select(selectTenantDetailState);
    }

    public getTenantDetailCompanies$(): Observable<IAumicoCompanyWithMapping[]> {
        return this.store.select(selectTenantDetailCompanies);
    }

    public isTenantDetailLoading$(): Observable<boolean> {
        return this.store.select(selectIsTenantDetailLoading);
    }

    public getTenantDetailError$(): Observable<string | null> {
        return this.store.select(selectTenantDetailError);
    }

    public loadMappedCompanies(connectionId: string): void {
        this.store.dispatch(integrationActions.loadMappedCompanies({ connectionId }));
    }

    // ── Connection mapping CRUD (per Company writes, ms-companies) ────────

    /**
     * Upsert the mapping for `(companyId, vendor)`. The effect calls
     * `PUT /api/companies/:id/integrations/mappings` and on success refreshes
     * the cached tenant detail so the manage modal table updates.
     */
    public upsertMapping(companyId: string, body: IUpsertConnectionMappingBody): void {
        this.store.dispatch(integrationActions.upsertMapping({ companyId, body }));
    }

    /**
     * Delete the mapping for `(companyId, vendor)`. Idempotent server side.
     */
    public deleteMapping(companyId: string, vendor: IntegrationVendor): void {
        this.store.dispatch(integrationActions.deleteMapping({ companyId, vendor }));
    }

    // ── Validate on read helper (Section C) ────────────────────────────────

    /**
     * `true` when the connection is loaded and healthy. Returns `false` for
     * deleted/disconnected/errored connections: the UI should render a red
     * "Re map" CTA in that case instead of the linked external company name.
     */
    public isConnectionHealthy$(connectionId: string): Observable<boolean> {
        return this.store.select(selectIsConnectionHealthyById(connectionId));
    }

    // ── Auto match (read only: only the post Connect effect triggers it) ──

    public isAutoMatchRunning$(): Observable<boolean> {
        return this.store.select(selectIsAutoMatchRunning);
    }

    /**
     * Banner state for the manage modal detail view. Returns null when no auto
     * match has run for this connection (or when it ran for a different one).
     */
    public getAutoMatchBanner$(
        connectionId: string,
    ): Observable<
        | null
        | { kind: 'running' }
        | { kind: 'error'; message: string }
        | { kind: 'success'; autoMatched: number; notMapped: number; errors: number }
    > {
        return this.store.select(selectAutoMatchBannerForConnection(connectionId));
    }

    // ── D365 BC journal import (mirrors Bexio's BexioProvider API) ─────────

    public loadD365BcFiscalYears(connectionId: string, externalCompanyId: string): void {
        this.store.dispatch(
            integrationActions.d365bcFiscalYearsLoad({ connectionId, externalCompanyId }),
        );
    }

    public getD365BcFiscalYears$(): Observable<IFiscalYear[]> {
        return this.store.select(selectD365BcFiscalYears);
    }

    public getD365BcFiscalYearsLoading$(): Observable<boolean> {
        return this.store.select(selectD365BcFiscalYearsLoading);
    }

    public runD365BcImport(body: ID365BcImportBody): void {
        this.store.dispatch(integrationActions.d365bcJournalImportRun({ body }));
    }

    public resetD365BcImport(): void {
        this.store.dispatch(integrationActions.d365bcJournalImportReset());
    }

    public getD365BcImportStatus$(): Observable<'idle' | 'loading' | 'success' | 'error'> {
        return this.store.select(selectD365BcImportStatus);
    }

    public getD365BcImportResult$(): Observable<IJournalImportResult | null> {
        return this.store.select(selectD365BcImportResult);
    }

    public getD365BcImportError$(): Observable<string | null> {
        return this.store.select(selectD365BcImportError);
    }

    // ── Abacus journal import ──────────────────────────────────────────────

    public runAbacusImport(body: IAbacusImportBody): void {
        this.store.dispatch(integrationActions.abacusJournalImportRun({ body }));
    }

    public resetAbacusImport(): void {
        this.store.dispatch(integrationActions.abacusJournalImportReset());
    }

    public getAbacusImportStatus$(): Observable<'idle' | 'loading' | 'success' | 'error'> {
        return this.store.select(selectAbacusImportStatus);
    }

    public getAbacusImportResult$(): Observable<IJournalImportResult | null> {
        return this.store.select(selectAbacusImportResult);
    }

    public getAbacusImportError$(): Observable<string | null> {
        return this.store.select(selectAbacusImportError);
    }

    // ── Topal journal import ───────────────────────────────────────────────

    public runTopalImport(body: ITopalImportBody): void {
        this.store.dispatch(integrationActions.topalJournalImportRun({ body }));
    }

    public resetTopalImport(): void {
        this.store.dispatch(integrationActions.topalJournalImportReset());
    }

    public getTopalImportStatus$(): Observable<'idle' | 'loading' | 'success' | 'error'> {
        return this.store.select(selectTopalImportStatus);
    }

    public getTopalImportResult$(): Observable<IJournalImportResult | null> {
        return this.store.select(selectTopalImportResult);
    }

    public getTopalImportError$(): Observable<string | null> {
        return this.store.select(selectTopalImportError);
    }

    // ── Accounto journal import ────────────────────────────────────────────

    /** `annualReportId` is the report the import is for; a late result is only applied there. */
    public runAccountoImport(body: IAccountoImportBody, annualReportId: string): void {
        this.store.dispatch(integrationActions.accountoJournalImportRun({ body, annualReportId }));
    }

    /** The whole slice incl. `importFor` — read it with `accountoImportResultForReport`. */
    public getAccountoJournalImportState$(): Observable<IAccountoJournalImportState> {
        return this.store.select(selectAccountoJournalImportState);
    }

    public resetAccountoImport(): void {
        this.store.dispatch(integrationActions.accountoJournalImportReset());
    }

    public getAccountoImportStatus$(): Observable<'idle' | 'loading' | 'success' | 'error'> {
        return this.store.select(selectAccountoImportStatus);
    }

    public getAccountoImportResult$(): Observable<IJournalImportResult | null> {
        return this.store.select(selectAccountoImportResult);
    }

    public getAccountoImportError$(): Observable<string | null> {
        return this.store.select(selectAccountoImportError);
    }

    // ── Proffix journal import ─────────────────────────────────────────────

    /** `annualReportId` is the report the import is for; a late result is only applied there. */
    public runProffixImport(body: IProffixImportBody, annualReportId: string): void {
        this.store.dispatch(integrationActions.proffixJournalImportRun({ body, annualReportId }));
    }

    /** The whole slice incl. `importFor` — read it with `proffixImportResultForReport`. */
    public getProffixJournalImportState$(): Observable<IProffixJournalImportState> {
        return this.store.select(selectProffixJournalImportState);
    }

    public resetProffixImport(): void {
        this.store.dispatch(integrationActions.proffixJournalImportReset());
    }

    public getProffixImportStatus$(): Observable<'idle' | 'loading' | 'success' | 'error'> {
        return this.store.select(selectProffixImportStatus);
    }

    public getProffixImportResult$(): Observable<IJournalImportResult | null> {
        return this.store.select(selectProffixImportResult);
    }

    public getProffixImportError$(): Observable<string | null> {
        return this.store.select(selectProffixImportError);
    }

    // ── SharePoint document source (REV-21679) ──────────────────────────────

    public getSharepointConnection$(): Observable<SharepointConnection & { connectionId?: string }> {
        return this.store.select(selectSharepointConnection);
    }

    public getSharepointFolders$(): Observable<SelectedClosingFolder[]> {
        return this.store.select(selectSharepointFolders);
    }

    public getSharepointLoading$(): Observable<boolean> {
        return this.store.select(selectSharepointLoading);
    }

    public getSharepointError$(): Observable<string | null> {
        return this.store.select(selectSharepointError);
    }

    /** BE-minted Microsoft File Picker token, once fetched (null before). */
    public getSharepointPickerToken$(): Observable<string | null> {
        return this.store.select(selectSharepointPickerToken);
    }

    /** Tenant SharePoint Online origin for the File Picker (null until known). */
    public getSharepointPickerBaseUrl$(): Observable<string | null> {
        return this.store.select(selectSharepointPickerBaseUrl);
    }

    /**
     * Terminal picker-token failure (null = none). Lets the container keep its pre-opened
     * popup alive while the token is merely in flight, and close it only when it failed.
     */
    public getSharepointPickerTokenError$(): Observable<string | null> {
        return this.store.select(selectSharepointPickerTokenError);
    }

    /** Kick off the Microsoft 365 admin-consent popup flow for an org-level SharePoint connection. */
    /**
     * REV-21860 — start the SharePoint connect from the ORG SETTINGS card.
     *
     * Org-scoped: no projectId (the BE builds the connection from the caller's
     * organization). Drives the generic `pendingSetup` slice, so the card's modal
     * reads `getPendingSetup$()` exactly like the D365 BC modal does.
     */
    public startSharepointOrgConnect(displayName?: string): void {
        this.store.dispatch(
            integrationActions.sharepointOrgConnectInitiate(
                displayName != null && displayName.length > 0 ? { displayName } : {},
            ),
        );
    }

    public startSharepointConnect(projectId: string): void {
        this.store.dispatch(integrationActions.sharepointConnectInitiate({ projectId }));
    }

    public loadSharepointConnection(projectId: string): void {
        this.store.dispatch(integrationActions.sharepointLoadConnection({ projectId }));
    }

    public loadSharepointFolders(projectId: string): void {
        this.store.dispatch(integrationActions.sharepointLoadFolders({ projectId }));
    }

    public addSharepointFolders(
        projectId: string,
        connectionId: string,
        folders: PickedSharepointFolder[],
    ): void {
        this.store.dispatch(
            integrationActions.sharepointAddFolders({ projectId, connectionId, folders }),
        );
    }

    public updateSharepointFolder(projectId: string, folderId: string, includeSubfolders: boolean): void {
        this.store.dispatch(integrationActions.sharepointUpdateFolder({ projectId, folderId, includeSubfolders }));
    }

    public removeSharepointFolder(projectId: string, folderId: string): void {
        this.store.dispatch(integrationActions.sharepointRemoveFolder({ projectId, folderId }));
    }

    /** Fetch a BE-minted picker token (dispatch path; result lands in the slice). */
    public getSharepointPickerToken(connectionId: string): void {
        this.store.dispatch(integrationActions.sharepointLoadPickerToken({ connectionId }));
    }
}
